import { Tier } from "@/design-system/Tags/MetaRatingChip/Tier/Tier";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { tierNameToTier } from "@/redux/players/playersSlice";
import type { SectionTier } from "@repo/shared/hooks";
import { useTranslation } from "@repo/shared/i18n";

const STAR = "★";

/** Design-system caret: points down when open, rotates to point right when collapsed. */
export const CompareCollapseArrow = ({
  open,
  className,
}: {
  open: boolean;
  className?: string;
}) => (
  <span
    className={cn(
      "flex size-3 items-center justify-center text-neutral-500 transition-transform",
      !open && "-rotate-90",
      className,
    )}
  >
    {arrow}
  </span>
);

/**
 * Collapsible section header ("PLAYSTYLES" / "ATTRIBUTES"): title on the left,
 * chevron on the far right, and an optional per-player tier row underneath
 * that grades the whole section on the shared column axis. The tiers come
 * from the comparison endpoint's category breakdown; a player without a tier
 * for the section shows a dash. The row is hidden until at least one tier is
 * known (e.g. before the first fetch resolves).
 *
 * `pinnedWidth` (mobile): the section lives inside a shared horizontal
 * scroller — the title row pins to the scroller's left edge at that width
 * while the tier row scrolls with the columns underneath.
 */
export const CompareSectionHeader = ({
  title,
  open,
  onToggle,
  gridStyle,
  tiers,
  className,
  pinnedWidth,
  hideTitle,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  gridStyle?: React.CSSProperties;
  /** Per-player tier + winner flag for this section, in column order. */
  tiers?: SectionTier[];
  className?: string;
  /** Sticky-left width for the title row inside a horizontal scroller. */
  pinnedWidth?: number;
  /**
   * Compact mode: drop the title row but keep the tier row. With no tiers to
   * show the header disappears entirely (the section adds its own top border).
   */
  hideTitle?: boolean;
}) => {
  const { t } = useTranslation("compare");
  const showTiers = Boolean(gridStyle && tiers?.some((tier) => tier.tier));
  if (hideTitle && !showTiers) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      // relative z-10 + solid bg lifts the header above the absolutely
      // positioned ColumnDividers, so no vertical lines cross the box.
      // Pinned (mobile): full-bleed — side borders would offset the
      // viewport-wide pinned title row by 1px into a phantom x-scroll.
      className={cn(
        "relative z-10 flex w-full flex-col bg-neutral-900",
        pinnedWidth != null
          ? "border-y border-neutral-800"
          : "rounded-t border border-neutral-800",
        className,
      )}
    >
      {/* Tailwind is compiled with important:true, so w-full would beat the
          inline pinned width — swap it out instead of overriding it. */}
      {!hideTitle && (
        <div
          className={cn(
            "flex items-center justify-between px-4 py-3",
            showTiers && "border-b border-neutral-800",
            pinnedWidth != null ? "sticky left-0 bg-neutral-900" : "w-full",
          )}
          style={pinnedWidth != null ? { width: pinnedWidth } : undefined}
        >
          <span className="text-sm font-extrabold uppercase tracking-[0.08em] text-neutral-200">
            {title}
          </span>
          <CompareCollapseArrow open={open} />
        </div>
      )}
      {showTiers && tiers && (
        <div className="relative grid w-full py-2" style={gridStyle}>
          {/* Column dividers, scoped to the tier row only (the header masks
              the page-level ones). */}
          {tiers.slice(0, -1).map((_, i) => (
            <div
              key={`divider-${i}`}
              className="pointer-events-none absolute inset-y-0 w-px bg-[#2e2e2e]"
              style={{ left: `${((i + 1) / tiers.length) * 100}%` }}
            />
          ))}
          {tiers.map(({ tier: tierName, winner }, idx) => {
            const tier = tierName ? tierNameToTier(tierName, true) : undefined;
            return (
              <div
                className="w-fit mx-auto flex items-center gap-x-1"
                key={idx}
              >
                {tier ? (
                  <Tier tier={tier} />
                ) : (
                  <span className="text-xs font-medium text-neutral-600">
                    –
                  </span>
                )}
                {winner && (
                  <span
                    className="text-xs leading-none text-white"
                    aria-label={t("sectionWinner")}
                  >
                    {STAR}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </button>
  );
};
